 
/**
 * Label Constants
 *
 * Centralized label maps for UI display values.
 * Uses proto enum format to match backend gRPC-Gateway serialization.
 */

// ============================================================================
// Account Labels
// ============================================================================

export type AccountType =
  | 'ACCOUNT_TYPE_ASSET'
  | 'ACCOUNT_TYPE_LIABILITY'
  | 'ACCOUNT_TYPE_EQUITY'
  | 'ACCOUNT_TYPE_REVENUE'
  | 'ACCOUNT_TYPE_EXPENSE';

export type NormalBalance =
  | 'NORMAL_BALANCE_DEBIT'
  | 'NORMAL_BALANCE_CREDIT';

export const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
  ACCOUNT_TYPE_ASSET: 'Asset',
  ACCOUNT_TYPE_LIABILITY: 'Liability',
  ACCOUNT_TYPE_EQUITY: 'Equity',
  ACCOUNT_TYPE_REVENUE: 'Revenue',
  ACCOUNT_TYPE_EXPENSE: 'Expense',
};

export const NORMAL_BALANCE_LABELS: Record<NormalBalance, string> = {
  NORMAL_BALANCE_DEBIT: 'Debit',
  NORMAL_BALANCE_CREDIT: 'Credit',
};

// ============================================================================
// Journal Entry Labels
// ============================================================================

export type EntryType =
  | 'ENTRY_TYPE_STANDARD'
  | 'ENTRY_TYPE_ADJUSTING'
  | 'ENTRY_TYPE_CLOSING'
  | 'ENTRY_TYPE_REVERSING'
  | 'ENTRY_TYPE_OPENING';

export type EntryStatus =
  | 'ENTRY_STATUS_DRAFT'
  | 'ENTRY_STATUS_POSTED'
  | 'ENTRY_STATUS_VOIDED';

export const ENTRY_TYPE_LABELS: Record<EntryType, string> = {
  ENTRY_TYPE_STANDARD: 'Standard',
  ENTRY_TYPE_ADJUSTING: 'Adjusting',
  ENTRY_TYPE_CLOSING: 'Closing',
  ENTRY_TYPE_REVERSING: 'Reversing',
  ENTRY_TYPE_OPENING: 'Opening',
};

export const ENTRY_STATUS_LABELS: Record<EntryStatus, string> = {
  ENTRY_STATUS_DRAFT: 'Draft',
  ENTRY_STATUS_POSTED: 'Posted',
  ENTRY_STATUS_VOIDED: 'Voided',
};

export const ENTRY_STATUS_COLORS: Record<EntryStatus, string> = {
  ENTRY_STATUS_DRAFT: 'yellow',
  ENTRY_STATUS_POSTED: 'green',
  ENTRY_STATUS_VOIDED: 'red',
};

// ============================================================================
// Fiscal Period Labels
// ============================================================================

export type PeriodType =
  | 'PERIOD_TYPE_MONTH'
  | 'PERIOD_TYPE_QUARTER'
  | 'PERIOD_TYPE_YEAR';

export type PeriodStatus =
  | 'PERIOD_STATUS_OPEN'
  | 'PERIOD_STATUS_CLOSED'
  | 'PERIOD_STATUS_LOCKED';

export const PERIOD_TYPE_LABELS: Record<PeriodType, string> = {
  PERIOD_TYPE_MONTH: 'Monthly',
  PERIOD_TYPE_QUARTER: 'Quarterly',
  PERIOD_TYPE_YEAR: 'Annual',
};

export const PERIOD_STATUS_LABELS: Record<PeriodStatus, string> = {
  PERIOD_STATUS_OPEN: 'Open',
  PERIOD_STATUS_CLOSED: 'Closed',
  PERIOD_STATUS_LOCKED: 'Locked',
};

export const PERIOD_STATUS_COLORS: Record<PeriodStatus, string> = {
  PERIOD_STATUS_OPEN: 'green',
  PERIOD_STATUS_CLOSED: 'yellow',
  PERIOD_STATUS_LOCKED: 'gray',
};

// ============================================================================
// Bank Account Types (India-specific)
// ============================================================================

/**
 * Bank account types for Indian businesses
 * - current: Current Account (for business transactions)
 * - savings: Savings Account
 * - od: Overdraft Account (borrowing facility)
 * - cc: Cash Credit Account (working capital facility)
 * - fd: Fixed Deposit
 * - cash: Cash on hand
 */
export type BankAccountType = 'current' | 'savings' | 'od' | 'cc' | 'fd' | 'cash';

export interface BankAccountTypeConfig {
  value: BankAccountType;
  label: string;
  subtype: string;
  codePrefix: string;
}

/**
 * Bank account type configuration
 * Single source of truth for bank account types
 */
export const BANK_ACCOUNT_TYPES: BankAccountTypeConfig[] = [
  { value: 'current', label: 'Current Account', subtype: 'Bank - Current', codePrefix: '1030' },
  { value: 'savings', label: 'Savings Account', subtype: 'Bank - Savings', codePrefix: '1040' },
  { value: 'od', label: 'Overdraft (OD) Account', subtype: 'Bank - OD', codePrefix: '1050' },
  { value: 'cc', label: 'Cash Credit (CC) Account', subtype: 'Bank - CC', codePrefix: '1060' },
  { value: 'fd', label: 'Fixed Deposit (FD)', subtype: 'Bank - FD', codePrefix: '1070' },
  { value: 'cash', label: 'Cash', subtype: 'Cash', codePrefix: '1010' },
];

/**
 * Get bank account type config by value
 */
export function getBankAccountTypeConfig(value: BankAccountType): BankAccountTypeConfig | undefined {
  return BANK_ACCOUNT_TYPES.find(t => t.value === value);
}

/**
 * Get subtype for a bank account type
 */
export function getBankAccountSubtype(value: BankAccountType): string {
  return getBankAccountTypeConfig(value)?.subtype ?? 'Cash';
}

/**
 * Get code prefix for a bank account type
 */
export function getBankAccountCodePrefix(value: BankAccountType): string {
  return getBankAccountTypeConfig(value)?.codePrefix ?? '1010';
}

/**
 * All account subtypes that can be used for payments/receipts
 * Used in account filters for quick entry forms
 */
export const PAYMENT_ACCOUNT_SUBTYPES = [
  'Cash',
  'Petty Cash',
  'Bank - Current',
  'Bank - Savings',
  'Bank - OD',
  'Bank - CC',
] as const;

// Bank account subtypes only (excludes Cash and Petty Cash)
export const BANK_ACCOUNT_SUBTYPES = [
  'Bank - Current',
  'Bank - Savings',
  'Bank - OD',
  'Bank - CC',
  'Bank - FD',
] as const;

/** Check if a subtype is a cash or bank type (used for bank detail fields in CoA) */
export function isCashOrBankSubtype(subtype: string | undefined): boolean {
  return (PAYMENT_ACCOUNT_SUBTYPES as readonly string[]).includes(subtype ?? '')
}

/** Check if a subtype is bank-only (excludes Cash — for invoice bank selection) */
export function isBankOnlySubtype(subtype: string | undefined): boolean {
  return (BANK_ACCOUNT_SUBTYPES as readonly string[]).includes(subtype ?? '')
}

/** Account subtype options for CoA modal (only ASSET type has cash/bank options) */
export const ACCOUNT_SUBTYPE_OPTIONS = [
  { value: '', label: 'General' },
  { value: 'Cash', label: 'Cash' },
  { value: 'Petty Cash', label: 'Petty Cash' },
  { value: 'Bank - Current', label: 'Bank - Current Account' },
  { value: 'Bank - Savings', label: 'Bank - Savings Account' },
  { value: 'Bank - OD', label: 'Bank - Overdraft (OD)' },
  { value: 'Bank - CC', label: 'Bank - Cash Credit (CC)' },
  { value: 'Bank - FD', label: 'Bank - Fixed Deposit (FD)' },
] as const;

export const EQUITY_SUBTYPE_OPTIONS = [
  { value: 'Capital', label: 'Owner Capital' },
  { value: 'Drawings', label: 'Owner Drawings' },
  { value: 'Retained Earnings', label: 'Retained Earnings' },
  { value: 'Opening Balance', label: 'Opening Balance' },
  { value: '', label: 'General' },
] as const;

/** Map account subtype to BankAccountType for bank_accounts table */
export function subtypeToBankAccountType(subtype: string): import('../services/bank-account-service').BankAccountType {
  const map: Record<string, import('../services/bank-account-service').BankAccountType> = {
    'Cash': 'cash',
    'Petty Cash': 'cash',
    'Bank - Current': 'current',
    'Bank - Savings': 'savings',
    'Bank - OD': 'od',
    'Bank - CC': 'cc',
    'Bank - FD': 'fd',
  }
  return map[subtype] ?? 'current'
}
